/** @jsxImportSource theme-ui */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box } from 'theme-ui';

export const FIT_VIEWPORT_HELP_TEXT =
    "When checked, the copied script tells the widget to stretch and stick so it fills the browser window once it's live on your website. This only affects the script you copy right now — it isn't saved with the configuration, and resets to checked every time you reload this page.";

// Sits inside a native <label> wrapping the fitViewport checkbox — any click
// inside a <label> forwards a synthetic click to its first form control, so
// the wrapper here must preventDefault/stopPropagation or clicking "?" would
// also toggle the checkbox.
//
// The popover itself is portaled to document.body and positioned with
// `fixed` viewport coordinates rather than nesting it under the trigger —
// the widget list page's scroll container (Container in components/styled.tsx)
// has overflowY: scroll, which would otherwise clip an absolutely-positioned
// popover the moment it extends past that container's visible area.
const FitViewportHelp: React.FC<{ text: string }> = ({ text }) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;
        const close = () => setOpen(false);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);

    const handleToggle = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) setCoords({ top: rect.bottom + 4, left: rect.left });
        setOpen((o) => !o);
    };

    return (
        <span
            sx={{ position: 'relative', display: 'inline-flex' }}
            onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            <button
                ref={buttonRef}
                type="button"
                onClick={handleToggle}
                onBlur={() => setOpen(false)}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'gray.5',
                    color: 'gray.5',
                    fontSize: '10px',
                    lineHeight: 1,
                    padding: 0,
                    bg: 'transparent',
                    cursor: 'pointer',
                }}
            >
                ?
            </button>
            {open &&
                coords &&
                createPortal(
                    <Box
                        sx={{
                            position: 'fixed',
                            top: `${coords.top}px`,
                            left: `${coords.left}px`,
                            width: '260px',
                            p: 2,
                            bg: 'background',
                            border: '1px solid',
                            borderColor: 'gray.3',
                            borderRadius: 'sm',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            fontSize: 1,
                            color: 'gray.7',
                            zIndex: 1000,
                        }}
                    >
                        {text}
                    </Box>,
                    document.body
                )}
        </span>
    );
};

export default FitViewportHelp;
